import React from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

interface SubprocessorItem {
  name: string;
  role: string;
  country: string;
}

const Legal: React.FC = () => {
  const { t } = useTranslation("legal");

  const subprocessors = (t("subprocessors.items", { returnObjects: true }) as SubprocessorItem[]) || [];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title={`${t("title")} | Frikopplad.nu`}
        description={t("intro")}
        url="/juridik"
      />
      <Navbar />
      <main className="flex-1">
        <article className="container-custom max-w-3xl py-16">
          <header className="mb-10">
            <h1 className="text-4xl md:text-5xl font-bold text-frikopplad-navy mb-4">
              {t("title")}
            </h1>
            <p className="text-lg text-gray-600">{t("intro")}</p>
          </header>

          <section className="mb-10" aria-labelledby="publisher-heading">
            <h2 id="publisher-heading" className="text-2xl font-bold text-frikopplad-navy mb-3">
              {t("publisher.title")}
            </h2>
            <address className="not-italic text-gray-700 space-y-1">
              <div className="font-semibold">{t("publisher.name")}</div>
              <div>{t("publisher.form")}</div>
              <div>{t("publisher.location")}</div>
              <div>
                <a
                  href="mailto:jtensetti@protonmail.com"
                  className="text-frikopplad-navy underline hover:text-frikopplad-red"
                >
                  {t("publisher.contact")}
                </a>
              </div>
            </address>
          </section>

          <section className="mb-10" aria-labelledby="data-heading">
            <h2 id="data-heading" className="text-2xl font-bold text-frikopplad-navy mb-3">
              {t("data.title")}
            </h2>
            <p className="text-gray-700 mb-3">{t("data.p1")}</p>
            <p className="text-gray-700 mb-3">{t("data.p2")}</p>
            <p className="text-gray-700">{t("data.p3")}</p>
          </section>

          <section className="mb-10" aria-labelledby="sub-heading">
            <h2 id="sub-heading" className="text-2xl font-bold text-frikopplad-navy mb-3">
              {t("subprocessors.title")}
            </h2>
            <p className="text-gray-700 mb-4">{t("subprocessors.intro")}</p>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-left text-sm">
                <thead className="bg-gray-50 text-frikopplad-navy">
                  <tr>
                    <th className="px-4 py-2 border-b border-gray-200">Provider</th>
                    <th className="px-4 py-2 border-b border-gray-200">Role</th>
                    <th className="px-4 py-2 border-b border-gray-200">Jurisdiction</th>
                  </tr>
                </thead>
                <tbody>
                  {subprocessors.map((item) => (
                    <tr key={item.name} className="odd:bg-white even:bg-gray-50">
                      <td className="px-4 py-2 border-b border-gray-100 font-medium">{item.name}</td>
                      <td className="px-4 py-2 border-b border-gray-100">{item.role}</td>
                      <td className="px-4 py-2 border-b border-gray-100">{item.country}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-10" aria-labelledby="code-heading">
            <h2 id="code-heading" className="text-2xl font-bold text-frikopplad-navy mb-3">
              {t("code.title")}
            </h2>
            <p className="text-gray-700 mb-3">{t("code.p1")}</p>
            <a
              href="https://codeberg.org/Tensetti/Frikopplad.nu/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-frikopplad-navy underline hover:text-frikopplad-red"
            >
              {t("code.link")} →
            </a>
          </section>

          <section className="mb-10" aria-labelledby="resp-heading">
            <h2 id="resp-heading" className="text-2xl font-bold text-frikopplad-navy mb-3">
              {t("responsibility.title")}
            </h2>
            <p className="text-gray-700">{t("responsibility.p1")}</p>
          </section>

          <p className="text-sm text-gray-500 mt-12">{t("updated")}</p>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default Legal;
